import { Constants } from "@constants";
import { createSlice } from "@reduxjs/toolkit";
import apiService from "@utils/apiService";
import { verifyApiResponse } from "@utils/apiResponseHelper";

const initialState = {
  loading: false,
  customUseCase: {},
  error: false,
  dropdownOptionsLoading: false,
  dropdownOptions: {},
  dropdownOptionsError: false,
  saveLoading: false,
  saveSuccess: false,
  saveError: false,
  deleteUseCaseLoading: false,
  deleteUseCaseError: false,
  deleteUseCaseSuccess: false,
  undoUseCaseLoading: false,
  undoUseCaseError: false,
  undoUseCaseSuccess: false
};

export const customUseCasesSlice = createSlice({
  name: "customUseCases",
  initialState,
  reducers: {
    customUseCasesStart: (state) => ({
      ...state,
      loading: true,
      error: false,
    }),
    customUseCasesSuccess: (state, action) => ({
      ...state,
      loading: false,
      customUseCase: action.payload,
    }),
    customUseCasesFailure: (state) => ({
      ...state,
      loading: false,
      error: true,
    }),
    customUseCasesDDOptionsStart: (state) => ({
      ...state,
      dropdownOptionsLoading: true,
      dropdownOptionsError: false,
    }),
    customUseCasesDDOptionsSuccess: (state, action) => ({
      ...state,
      dropdownOptionsLoading: false,
      dropdownOptions: action.payload,
    }),
    customUseCasesDDOptionsFailure: (state) => ({
      ...state,
      dropdownOptionsLoading: false,
      dropdownOptionsError: true,
    }),
    saveCustomUseCasesStart: (state) => ({
      ...state,
      saveLoading: true,
      saveError: false,
    }),
    saveCustomUseCasesSuccess: (state, action) => ({
      ...state,
      saveLoading: false,
      saveSuccess: true,
      customUseCase: action.payload,
    }),
    saveCustomUseCasesFailure: (state) => ({
      ...state,
      saveLoading: false,
      saveError: true,
    }),
    deleteCustomUseCasesStart: (state) => ({
      ...state,
      deleteUseCaseLoading: true,
      deleteUseCaseError: false,
    }),
    deleteCustomUseCasesSuccess: (state) => ({
      ...state,
      deleteUseCaseLoading: false,
      deleteUseCaseSuccess: true,
    }),
    deleteCustomUseCasesFailure: (state) => ({
      ...state,
      deleteUseCaseLoading: false,
      deleteUseCaseError: true,
    }),
    undoCustomUseCaseStart: (state) => ({
      ...state,
      undoUseCaseLoading: true,
      undoUseCaseError: false,
    }),
    undoCustomUseCaseSuccess: (state) => ({
      ...state,
      undoUseCaseLoading: false,
      undoUseCaseSuccess: true,
    }),
    undoCustomUseCaseFailure: (state) => ({
      ...state,
      undoUseCaseLoading: false,
      undoUseCaseError: true,
    }),
    resetCustomUseCases: () => ({
      ...initialState,
    }),
  },
});

const { actions, reducer } = customUseCasesSlice;

export const { 
  customUseCasesStart,
  customUseCasesSuccess,
  customUseCasesFailure,
  customUseCasesDDOptionsStart,
  customUseCasesDDOptionsSuccess,
  customUseCasesDDOptionsFailure,
  saveCustomUseCasesStart,
  saveCustomUseCasesSuccess,
  saveCustomUseCasesFailure,
  deleteCustomUseCasesStart,
  deleteCustomUseCasesSuccess,
  deleteCustomUseCasesFailure,
  undoCustomUseCaseStart,
  undoCustomUseCaseSuccess,
  undoCustomUseCaseFailure,
  resetCustomUseCases,
} = actions;

export const getCustomUseCaseInfo = (payload) => (dispatch) => {
  dispatch(customUseCasesStart());
  apiService
    .get(`${Constants.API.SCORE_CARD}/${payload?.scorecardId}/${Constants.API.CUSTOM_USE_CASES}/${payload?.useCaseId}`)
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success && Object.keys(data?.data).length) {
        dispatch(customUseCasesSuccess(data?.data));
      } else {
        dispatch(customUseCasesFailure());
      }
    })
    .catch(() => {
      dispatch(customUseCasesFailure());
    });
}

export const getCustomUseCasesDDOptions = (scorecardId) => (dispatch) => {
  dispatch(customUseCasesDDOptionsStart());
  apiService
    .get(`${Constants.API.SCORE_CARD}/${scorecardId}/${Constants.API.CUSTOM_USE_CASES}/${Constants.API.CUSTOM_USE_CASES_DD_OPTIONS}`)
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success && Object.keys(data?.data).length) {
        dispatch(customUseCasesDDOptionsSuccess(data?.data));
      } else {
        dispatch(customUseCasesDDOptionsFailure());
      }
    })
    .catch(() => {
      dispatch(customUseCasesDDOptionsFailure());
    });
}

export const saveCustomUseCaseInfo = (payload, updatedUseCaseObj) => (dispatch, getState) => {
  dispatch(saveCustomUseCasesStart());
  apiService
    .put(
      `${Constants.API.SCORE_CARD}/${payload?.scorecardId}/${Constants.API.CUSTOM_USE_CASES}/${payload?.useCaseId}`,
      updatedUseCaseObj
    )
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success && Object.keys(data?.data).length) {
        const state = getState();
        dispatch(saveCustomUseCasesSuccess({
          ...state.customUseCases.customUseCase,
          ...data?.data,
        }));
      } else {
        dispatch(saveCustomUseCasesFailure());
      }
    })
    .catch(() => {
      dispatch(saveCustomUseCasesFailure());
    });
}

export const deleteCustomUseCase = (payload) => (dispatch) => {
  dispatch(deleteCustomUseCasesStart());
  apiService
    .delete(`${Constants.API.SCORE_CARD}/${payload.scorecardId}/${Constants.API.CUSTOM_USE_CASES}/${payload.useCaseId}`)
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success) {
        dispatch(deleteCustomUseCasesSuccess());
      } else {
        dispatch(deleteCustomUseCasesFailure());
      }
    })
    .catch(() => {
      dispatch(deleteCustomUseCasesFailure());
    });
}

export const undoCustomUseCase = (payload) => (dispatch) => {
  dispatch(undoCustomUseCaseStart());
  apiService
    .post(`${Constants.API.SCORE_CARD}/${payload.scorecardId}/${Constants.API.CUSTOM_USE_CASES}/${payload.useCaseId}/${Constants.API.CUSTOM_USE_CASES_UNDO}`)
    .then((response) => {
      let data = verifyApiResponse(response);
      if (data?.systemMessage?.success) {
        dispatch(undoCustomUseCaseSuccess());
      } else {
        dispatch(undoCustomUseCaseFailure());
      }
    })
    .catch(() => {
      dispatch(undoCustomUseCaseFailure());
    });
}

export default reducer;
